/**
 * B-24b WS-3 — SA dashboard API client.
 *
 * Typed wrappers around the new `/admin/sa-dashboard/*` endpoints defined by
 * the WS-2 engineer contract. WS-2 is in flight; all responses below are
 * mocked by the test layer (no MSW — vi.mock at apiClient level, matching the
 * existing `dashboardApi` test pattern).
 *
 * IMPORTANT: every field name / shape here is the CANONICAL contract that
 * WS-2 must implement. If WS-2 diverges, fix the backend, not this file.
 *
 * Two-zone boundary note:
 *   - `fetchSaKpis` carries the platform-aggregate bias_pass_rate (platform
 *     zone). The value is a cross-tenant scalar — it is never co-keyed by
 *     tenant_id anywhere in this module.
 *   - All other endpoints carry tenant-zone named data. They MUST NOT source
 *     values from `platform_rating_signals` (invariant IV-Z5).
 */
import { apiClient } from "@/lib/apiClient";

// ── KPI strip ────────────────────────────────────────────────────────────────

/**
 * `GET /admin/sa-dashboard/kpis`
 *
 * `delta: null` means no prior period exists — UI renders the value only,
 * no delta badge.
 * `bias_pass_rate.insufficient_sample` → render "building baseline" placeholder.
 */
export interface SaKpisResponse {
  active_customers: { value: number; delta: number | null };
  arr_cents: { value: number; delta: number | null };
  bias_pass_rate: {
    value: number | null;
    delta: number | null;
    insufficient_sample: boolean;
  };
  needs_attention: { value: number };
}

export async function fetchSaKpis(): Promise<SaKpisResponse> {
  const r = await apiClient.get<SaKpisResponse>("/admin/sa-dashboard/kpis");
  return r.data;
}

// ── Attention list ───────────────────────────────────────────────────────────

/**
 * `GET /admin/sa-dashboard/attention`
 *
 * `status: "all_clear"` → render calm green banner; no item list.
 * Items are tenant-zone and nameable.
 */
export interface AttentionItem {
  tenant_id: string;
  name: string;
  reason: string;
  severity: "critical" | "warning";
  recommended_action: string;
}

export interface SaAttentionResponse {
  status: "all_clear" | "items";
  items: AttentionItem[];
}

export async function fetchSaAttention(): Promise<SaAttentionResponse> {
  const r = await apiClient.get<SaAttentionResponse>(
    "/admin/sa-dashboard/attention",
  );
  return r.data;
}

// ── Usage trend ──────────────────────────────────────────────────────────────

/**
 * `GET /admin/sa-dashboard/usage-trend`
 *
 * Tenant-zone. `available: 0` guard: UI renders "—" for pct_of_allotment.
 * Carries credit consumption folded in (no standalone credits tile).
 */
interface UsageTrendWeek {
  week_bucket: string;
  consumed: number;
  available: number;
  // null when entitlement is 0/unknown — UI renders "—" (P1-5). The backend
  // already yields null in that case (sa_dashboard.get_usage_trend).
  pct_of_allotment: number | null;
}

export interface UsageTrendTenant {
  tenant_id: string;
  name: string;
  weekly: UsageTrendWeek[];
}

export interface SaUsageTrendResponse {
  tenants: UsageTrendTenant[];
}

export async function fetchSaUsageTrend(): Promise<SaUsageTrendResponse> {
  const r = await apiClient.get<SaUsageTrendResponse>(
    "/admin/sa-dashboard/usage-trend",
  );
  return r.data;
}

// ── Calibration health ───────────────────────────────────────────────────────

/**
 * `GET /admin/sa-dashboard/calibration-health`
 *
 * Tenant-zone. `bias_pass_rate: null` → "insufficient sample" placeholder.
 * `drift: true` → tenant is behind the platform-current guardrails version.
 */
export interface CalibrationHealthTenant {
  tenant_id: string;
  name: string;
  calibrated: boolean;
  bias_pass_rate: number | null;
  guardrails_version: string;
  drift: boolean;
}

export interface SaCalibrationHealthResponse {
  tenants: CalibrationHealthTenant[];
}

export async function fetchSaCalibrationHealth(): Promise<SaCalibrationHealthResponse> {
  const r = await apiClient.get<SaCalibrationHealthResponse>(
    "/admin/sa-dashboard/calibration-health",
  );
  return r.data;
}

// ── Account health ───────────────────────────────────────────────────────────

/**
 * `GET /admin/sa-dashboard/account-health`
 *
 * Tenant-zone. ARR + renewal date from new `tenant_contract` table; NOT from
 * Stripe `Subscription`. `contract_value_cents: null` = no contract on file.
 * `renewal_date: null` = no contract term end recorded.
 */
export interface AccountHealthTenant {
  tenant_id: string;
  name: string;
  contract_value_cents: number | null;
  renewal_date: string | null;
  status: string;
  churn_risk: "low" | "medium" | "high" | "unknown";
}

export interface SaAccountHealthResponse {
  tenants: AccountHealthTenant[];
}

export async function fetchSaAccountHealth(): Promise<SaAccountHealthResponse> {
  const r = await apiClient.get<SaAccountHealthResponse>(
    "/admin/sa-dashboard/account-health",
  );
  return r.data;
}

// ── Per-request cost drill-down ────────────────────────────────────────────────

/**
 * `GET /admin/usage/rows?request_id=…`
 *
 * The individual `usage_ledger` rows for ONE request — each AI action (eval,
 * rank_one, resume_format, …) with its own token + cost breakdown. Read-only,
 * SA cross-tenant (gated by `current_super_admin_not_impersonating`).
 *
 * `cost_usd_estimate: null` means the model was unknown to the cost table —
 * render "—", do NOT show $0.
 *
 * Scope: filtered by `request_id` ONLY (no per-candidate filter — `usage_ledger`
 * has no `candidate_id` column; that would need new instrumentation).
 */
export interface UsageRow {
  action: string;
  model: string;
  prompt_name: string;
  input_tokens: number;
  output_tokens: number;
  cached_read_tokens: number;
  cached_write_tokens: number;
  cost_usd_estimate: number | null;
  duration_ms: number;
  outcome: string;
  request_id: string | null;
  tenant_id: string;
  user_id: string | null;
  created_at: string;
}

export interface SaUsageRowsResponse {
  request_id: string;
  rows: UsageRow[];
}

export async function fetchSaUsageRows(
  requestId: string,
): Promise<SaUsageRowsResponse> {
  const r = await apiClient.get<SaUsageRowsResponse>("/admin/usage/rows", {
    params: { request_id: requestId },
  });
  return r.data;
}
